import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event.
   *
   * AANGEPAST (Velo Goods): standaard verving Horizon hier de complete galerij,
   * waardoor de foto van de gekozen kleur vooraan kwam te staan en de galerij
   * leek te "verversen". Nu schuiven we in plaats daarvan naar de foto die al
   * in de galerij staat. Alleen als dat niet kan (ander product geladen, andere
   * set foto's, of geen gekoppelde foto) vallen we terug op het oude gedrag.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // De foto die aan de gekozen variant (kleur) is gekoppeld in Shopify.
    const featuredMediaId = event.detail.resource?.featured_media?.id;

    // Bij een "combined listing" wisselt de hele productpagina; dan klopt
    // onze bestaande galerij niet meer en moeten we wél vervangen.
    const productChanged = Boolean(event.detail.data.newProduct);

    // Veiligheidscheck: alleen schuiven als de nieuwe galerij evenveel foto's
    // heeft als de huidige (bijv. met "verberg variant-foto's" aan verandert
    // de set foto's per kleur, en dan is vervangen de juiste route).
    const currentSlides = this.slideshow?.slides ?? [];
    const newSlideCount = newMediaGallery.querySelectorAll('slideshow-slide').length;

    if (!productChanged && featuredMediaId && currentSlides.length === newSlideCount) {
      // Zoek de foto van de gekozen kleur in de galerij zoals die er nu staat.
      const targetIndex = currentSlides.findIndex((slide) =>
        slide.querySelector(`[data-media-id="${featuredMediaId}"]`)
      );

      if (targetIndex >= 0) {
        // Schuif soepel naar die foto. De galerij blijft verder onaangeraakt.
        this.slideshow?.select(targetIndex, undefined, { animate: true });
        return;
      }
    }

    // Terugvaloptie: het originele Horizon-gedrag (galerij vervangen).
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
